import React, { useState } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { 
  Palette, Plus, Trash2, Sparkles, MoveUp, MoveDown
} from 'lucide-react';
import { 
  getActiveColorBarPatches, 
  STANDARD_CMYK_PATCHES, 
  CMYK_TINTS_PATCHES, 
  CMYK_RGB_GRAY_PATCHES, 
  HEXACHROME_PATCHES, 
  SPOT_BRAND_SAMPLE_PATCHES 
} from '../../utils/colorBarsUtils';
import { ColorBarPatch } from '../../types';
import { formatUnit, parseUnit } from '../../utils/units';

interface ColorBarsConfigPanelProps {
  isModal?: boolean;
  onClose?: () => void;
}

export const ColorBarsConfigPanel: React.FC<ColorBarsConfigPanelProps> = ({ isModal, onClose }) => {
  const { layout, setLayout, appUnit } = useSaaSContext();
  const [newColor, setNewColor] = useState('#ef3340');
  const [newName, setNewName] = useState('Spot Red');

  const currentPatches = getActiveColorBarPatches(layout);
  const showNames = layout.colorBarsShowNames ?? false;

  const updatePatches = (newPatches: ColorBarPatch[]) => {
    setLayout(prev => ({
      ...prev,
      colorBars: true,
      colorBarsPatches: newPatches,
      colorBarsPreset: 'spot_custom'
    }));
  };

  const handlePresetSelect = (presetKey: 'standard_cmyk' | 'cmyk_tints' | 'cmyk_rgb' | 'hexachrome' | 'spot_custom') => {
    let patches: ColorBarPatch[] = STANDARD_CMYK_PATCHES;
    if (presetKey === 'cmyk_tints') patches = CMYK_TINTS_PATCHES;
    if (presetKey === 'cmyk_rgb') patches = CMYK_RGB_GRAY_PATCHES;
    if (presetKey === 'hexachrome') patches = HEXACHROME_PATCHES;
    if (presetKey === 'spot_custom') patches = SPOT_BRAND_SAMPLE_PATCHES;

    setLayout(prev => ({
      ...prev,
      colorBars: true,
      colorBarsPreset: presetKey,
      colorBarsPatches: patches
    }));
  };

  const handleAddPatch = () => {
    const patchId = `patch_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const newPatch: ColorBarPatch = {
      id: patchId,
      name: newName.trim() || `Color ${currentPatches.length + 1}`,
      color: newColor
    };
    updatePatches([...currentPatches, newPatch]);
    setNewName(`Spot ${currentPatches.length + 2}`);
  };

  const handleUpdatePatch = (id: string, updates: Partial<ColorBarPatch>) => {
    const updated = currentPatches.map(p => p.id === id ? { ...p, ...updates } : p);
    updatePatches(updated);
  };

  const handleRemovePatch = (id: string) => {
    if (currentPatches.length <= 1) return;
    const filtered = currentPatches.filter(p => p.id !== id);
    updatePatches(filtered);
  };

  const handleMovePatch = (index: number, direction: 'up' | 'down') => {
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= currentPatches.length) return;
    const copy = [...currentPatches];
    const temp = copy[index];
    copy[index] = copy[newIdx];
    copy[newIdx] = temp;
    updatePatches(copy);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700/80 rounded-xl p-4 text-zinc-100 shadow-2xl flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-600/20 text-blue-400 rounded-lg border border-blue-500/30">
            <Palette className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
              CMYK & Custom Color Bars
              <span className="text-[10px] bg-blue-500/20 text-blue-300 font-mono px-1.5 py-0.5 rounded border border-blue-400/30">Prepress</span>
            </h3>
            <p className="text-[11px] text-zinc-400">Control press density control patches, custom spot colors, and color name labels</p>
          </div>
        </div>
        {isModal && onClose && (
          <button 
            onClick={onClose}
            className="text-zinc-400 hover:text-white px-2 py-1 rounded bg-zinc-800 text-xs font-semibold"
          >
            ✕
          </button>
        )}
      </div>

      {/* Live Preview Strip */}
      <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800 flex flex-col gap-2">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span className="font-semibold text-zinc-300 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            Live Color Bar Strip ({currentPatches.length} patches)
          </span>
          <span className="text-[11px] font-mono text-zinc-400">
            {layout.colorBarsPosition || 'top_center'}
          </span>
        </div>

        {/* Color Bar Rendering */}
        <div className="flex items-center gap-1 p-2 bg-zinc-900/90 rounded border border-zinc-700/60 overflow-x-auto shadow-inner min-h-[52px]">
          {currentPatches.map((patch) => (
            <div key={patch.id} className="flex flex-col items-center gap-1 group flex-shrink-0">
              <div 
                className="w-7 h-7 rounded border border-white/20 shadow-xs relative transition-transform group-hover:scale-105"
                style={{ backgroundColor: patch.color }}
                title={`${patch.name}: ${patch.color}`}
              />
              {showNames && (
                <span className="text-[9px] font-mono text-zinc-300 max-w-[42px] truncate px-0.5" title={patch.name}>
                  {patch.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Show Color Names Toggle & Patch Size */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-zinc-950/60 p-3 rounded-lg border border-zinc-800">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input 
            type="checkbox"
            className="w-4 h-4 rounded border-zinc-700 accent-blue-600 cursor-pointer"
            checked={showNames}
            onChange={e => setLayout(prev => ({ ...prev, colorBarsShowNames: e.target.checked }))}
          />
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-zinc-200">Show Color Names / Labels</span>
            <span className="text-[10px] text-zinc-400">Prints text labels (C, M, Y, K, Spot name) beside patches</span>
          </div>
        </label>

        <div>
          <label className="text-xs font-medium text-zinc-300 mb-1 flex items-center justify-between">
            <span>Patch Size</span>
            <span className="text-[10px] font-mono text-blue-400">{layout.colorBarsPatchSize || 8} pt</span>
          </label>
          <div className="flex items-center gap-2">
            <input 
              type="range"
              min="4"
              max="20"
              step="1"
              value={layout.colorBarsPatchSize || 8}
              onChange={e => setLayout(prev => ({ ...prev, colorBarsPatchSize: Number(e.target.value) }))}
              className="w-full accent-blue-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Position & Offset */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-zinc-950/60 p-3 rounded-lg border border-zinc-800">
        <div>
          <label className="text-[11px] font-semibold text-zinc-300 mb-1 block">Bar Placement</label>
          <select
            className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded-md text-xs outline-none focus:ring-2 focus:ring-blue-500 font-medium"
            value={layout.colorBarsPosition || 'top_center'}
            onChange={e => setLayout(prev => ({ ...prev, colorBarsPosition: e.target.value as any }))}
          >
            <option value="top_center">Top Margin (Center)</option>
            <option value="top_left">Top Margin (Left)</option>
            <option value="top_right">Top Margin (Right)</option>
            <option value="bottom_center">Bottom Margin (Center)</option>
            <option value="bottom_left">Bottom Margin (Left)</option>
            <option value="bottom_right">Bottom Margin (Right)</option>
            <option value="left">Left Margin (Vertical)</option>
            <option value="right">Right Margin (Vertical)</option>
          </select>
        </div>

        <div>
          <label className="text-[11px] font-semibold text-zinc-300 mb-1 block">Sheet Margin Offset ({appUnit})</label>
          <input 
            type="number"
            step="0.5"
            min="0"
            className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded-md text-xs font-mono outline-none focus:ring-2 focus:ring-blue-500"
            value={layout.colorBarsOffset !== undefined && !isNaN(layout.colorBarsOffset) ? formatUnit(layout.colorBarsOffset, appUnit) : ''}
            onChange={e => setLayout(prev => ({ ...prev, colorBarsOffset: parseUnit(e.target.value, appUnit) }))}
            placeholder="e.g. 3 mm"
          />
        </div>
      </div>

      {/* Preset Selector Tabs */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-zinc-300 flex items-center justify-between">
          <span>Palette Presets</span>
          <span className="text-[11px] text-zinc-400">Click to load standard press palettes</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handlePresetSelect('standard_cmyk')}
            className={`p-2.5 rounded-lg border text-xs font-medium text-left transition-all ${
              layout.colorBarsPreset === 'standard_cmyk' || !layout.colorBarsPreset
                ? 'bg-blue-600/30 border-blue-500 text-blue-200 shadow-sm'
                : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            <div className="font-semibold text-xs text-white">4-Color CMYK</div>
            <div className="flex gap-1.5 mt-1.5">
              <span className="w-3 h-3 rounded-xs bg-cyan-400 inline-block"></span>
              <span className="w-3 h-3 rounded-xs bg-fuchsia-500 inline-block"></span>
              <span className="w-3 h-3 rounded-xs bg-yellow-300 inline-block"></span>
              <span className="w-3 h-3 rounded-xs bg-black inline-block"></span>
            </div>
          </button>

          <button
            onClick={() => handlePresetSelect('cmyk_tints')}
            className={`p-2.5 rounded-lg border text-xs font-medium text-left transition-all ${
              layout.colorBarsPreset === 'cmyk_tints'
                ? 'bg-blue-600/30 border-blue-500 text-blue-200 shadow-sm'
                : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            <div className="font-semibold text-xs text-white">CMYK + Tints</div>
            <div className="text-[10px] text-zinc-400 truncate mt-1">100%, 75%, 50%, 25%</div>
          </button>

          <button
            onClick={() => handlePresetSelect('cmyk_rgb')}
            className={`p-2.5 rounded-lg border text-xs font-medium text-left transition-all ${
              layout.colorBarsPreset === 'cmyk_rgb'
                ? 'bg-blue-600/30 border-blue-500 text-blue-200 shadow-sm'
                : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            <div className="font-semibold text-xs text-white">CMYK + RGB + Gray</div>
            <div className="text-[10px] text-zinc-400 truncate mt-1">8-Color Density Solid</div>
          </button>

          <button
            onClick={() => handlePresetSelect('hexachrome')}
            className={`p-2.5 rounded-lg border text-xs font-medium text-left transition-all ${
              layout.colorBarsPreset === 'hexachrome'
                ? 'bg-blue-600/30 border-blue-500 text-blue-200 shadow-sm'
                : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            <div className="font-semibold text-xs text-white">6-Color Hexachrome</div>
            <div className="text-[10px] text-zinc-400 truncate mt-1">CMYK + Orange + Green</div>
          </button>
        </div>
      </div>

      {/* Swatches Editor / List */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-zinc-200">
            Customize Color Patches ({currentPatches.length})
          </label>
          <span className="text-[11px] text-zinc-400">Click color or text to edit</span>
        </div>

        {/* List of active swatches with direct edit */}
        <div className="max-h-56 overflow-y-auto pr-1 flex flex-col gap-1.5 bg-zinc-950 p-2 rounded-lg border border-zinc-800">
          {currentPatches.map((patch, idx) => (
            <div 
              key={patch.id} 
              className="flex items-center gap-2 p-1.5 bg-zinc-900 border border-zinc-800 rounded-md hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-center gap-1 text-zinc-500">
                <button 
                  onClick={() => handleMovePatch(idx, 'up')}
                  disabled={idx === 0}
                  className="hover:text-zinc-200 disabled:opacity-20 p-0.5"
                  title="Move patch left/up"
                >
                  <MoveUp className="w-3 h-3" />
                </button>
                <button 
                  onClick={() => handleMovePatch(idx, 'down')}
                  disabled={idx === currentPatches.length - 1}
                  className="hover:text-zinc-200 disabled:opacity-20 p-0.5"
                  title="Move patch right/down"
                >
                  <MoveDown className="w-3 h-3" />
                </button>
              </div>

              {/* Color Picker Input */}
              <input 
                type="color"
                value={patch.color.startsWith('#') ? patch.color : '#00ffff'}
                onChange={e => handleUpdatePatch(patch.id, { color: e.target.value })}
                className="w-7 h-7 rounded cursor-pointer border border-zinc-600 bg-transparent p-0 flex-shrink-0"
                title="Change patch color"
              />

              {/* Name Editor */}
              <input 
                type="text"
                value={patch.name}
                onChange={e => handleUpdatePatch(patch.id, { name: e.target.value })}
                className="flex-1 min-w-0 px-2.5 py-1 bg-zinc-950 border border-zinc-800 text-zinc-100 rounded text-xs outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                placeholder="Color / Spot Name"
              />

              {/* Hex Display */}
              <span className="text-[10px] font-mono text-zinc-400 shrink-0 text-right">
                {patch.color}
              </span>

              {/* Delete */}
              <button 
                onClick={() => handleRemovePatch(patch.id)}
                disabled={currentPatches.length <= 1}
                className="text-zinc-400 hover:text-red-400 disabled:opacity-20 p-1 rounded hover:bg-zinc-800 transition-colors shrink-0"
                title="Remove color patch"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Add New Custom Patch Input Form */}
        <div className="flex items-center gap-2 bg-zinc-950/80 p-2.5 rounded-lg border border-zinc-800 mt-1">
          <input 
            type="color"
            value={newColor}
            onChange={e => setNewColor(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer border border-zinc-600 bg-transparent p-0 shrink-0"
            title="Pick color"
          />
          <input 
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Color / Spot Name (e.g. Pantone 032 C, Gold)"
            className="flex-1 min-w-0 px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs outline-none focus:ring-1 focus:ring-blue-500 font-medium"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddPatch();
              }
            }}
          />
          <button 
            onClick={handleAddPatch}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded flex items-center gap-1.5 shadow-sm transition-colors shrink-0 whitespace-nowrap cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Color</span>
          </button>
        </div>
      </div>

      {/* Footer Action Buttons */}
      <div className="pt-2 border-t border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[10px] text-zinc-500 font-mono">Live Color Bars Active</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            Apply & Close
          </button>
        )}
      </div>
    </div>
  );
};
